import { Dictionary, Id, RegisteredIp } from "./core"
import { CloudProvider } from "./global"
import { WizardConfigItem, WizardWidget } from "./wizard"
import { OrganizationCertificateStatus } from "./organization"
import { DiscriminationKey } from "@/utils/space-discriminator"

export enum WorkspaceStatusEnum {
  Idle = "idle",
  Draft = "draft",
  Aborting = "aborting",
  Deploying = "deploying",
  Deployed = "deployed",
  DeployFailed = "deploy-failed",
  Destroying = "destroying",
  DestroyFailed = "destroy-failed",
  DeployedWithError = "deployed-with-error",
  ArchiveFailed = "archive-failed",
  Archiving = "archiving",
  Restoring = "restoring",
  Archived = "archived",
  DeployPending = "deploy-pending",
  /** @deprecated */
  ValidationNoAction = "validation-no-action"
}

export enum WorkspaceSubStatus {
  PrepareDeploying = "prepare-deploying",
  DeployStarted = "deploy-started",
  DestroyingStarted = "destroying-started",
  WaitingForDeployment = "waiting-for-deployment",
  WaitingForDestruction = "waiting-for-destruction",
  FinishingUpEverything = "finishing-up-everything"
}

export enum WorkspaceOnlineStatusEnum {
  Starting = "starting",
  Online = "online",
  Stopping = "stopping",
  Offline = "offline",
  Aborting = "aborting",
  Archived = "archived"
}

export enum WorkspaceErrorLevelEnum {
  Error = "error",
  Warning = "warning"
}

export enum SuitePlanEnum {
  Ultimate = "Ultimate",
  Premium = "Premium",
  Essential = "Essential"
}

export interface SortingConfig {
  sortBy: string[]
  sortDesc: boolean[]
}

export interface IAssetConfigurationDefinition {
  type: AssetConfigurationType
  name: string
  defaultValue?: string
}

export interface CreateWorkspacePayload {
  templateId: string
  authorizedIps: RegisteredIp[]
  workspaceWizardConfiguration?: WizardConfigItem[]
  organizationId: string
  title?: string
  description?: string
  tags?: string[]
}

export type AssetConfigurationType = "string" | "files" | null

export interface IAssetConfiguration {
  type: AssetConfigurationType
  key: string
  value: string
  name: string
}

export interface ApplicationLink {
  label: string
  link: string
  ipRequired: boolean
  credentials: {
    username: string
    password: string
  } | null
}

export interface IWorkspaceAsset {
  _discriminator?: DiscriminationKey
  id: string
  type: string
  description: string
  subtitle: string
  applicationLinks: ApplicationLink[]
  configurations: IAssetConfiguration[]
  allowedInstanceTypes: IInstanceType[]
  deployOutputs: IAssetConfiguration[]
  onlineStatus: WorkspaceOnlineStatusEnum
  userConfigurations: WizardConfigItem[] | null
  capacityReservation: SpaceCapacityReservation | null
  deployedInstanceType?: IInstanceType | null
  runningSince: string | null
  onlineError: string | null
  instancePlatform: (InstancePlatform & string) | null
}

export interface CapacityReservation {
  region: string
  availabilityZone: string
  instanceType: string
  capacityReservationId: string
}

export enum CapacityReservationType {
  VIZ_NOW_MANAGED = "VizNowManaged",
  BRING_YOUR_OWN = "BringYourOwn"
}

export interface SpaceCapacityReservation {
  type: `${CapacityReservationType}`
  reservationId: string
  endDate: string | null
  instanceType: IInstanceType
  hasCapacityReservation: boolean
}

export enum InstancePlatform {
  linux = "Linux",
  windows = "Windows"
}

export interface IWorkspaceSchema {
  assets: IWorkspaceAsset[]
  missingDependencies: boolean
  services: Service[]
  tags: SpaceTag[]
}

export interface IWorkspacePermissions {
  userId: string
  canEdit: boolean
}

export interface IAuthorizedIp {
  ip: string
  description: string
  createdByUserId?: string
  creationDate?: Date
}

export enum CertificateVisibleState {
  warning = "warning",
  danger = "danger",
  notVisible = "notVisible"
}

export enum CertificateStatus {
  newCertificateAvailable = "newCertificateAvailable",
  certificateIncluded = "certificateIncluded",
  notAvailable = "notAvailable",
  organizationCertificateExpired = "organizationCertificateExpired",
  workspaceCertificateExpired = "workspaceCertificateExpired",
  certificateAvailable = "certificateAvailable",
  validating = "validating",
  failed = "failed"
}

export interface CertificateInfo<Status = CertificateStatus | OrganizationCertificateStatus | null> {
  expiresAt: string | null
  status: Status
  visibleState: CertificateVisibleState
  certificateError: string | null
}

export interface SpaceTag {
  name: string
  value: string
}

export interface AutoStartupDefinition {
  scheduledToStartupAt: string | null
  scheduledAt: string | null
}

export interface WorkspaceTemplate {
  id: string
  title: string
  templateId: string
  plan: SuitePlanEnum
  description: string
  organizationId: string
  username: string
  cloudZone: string
  cloudZoneAZ: string
  instructions: string
  schema: IWorkspaceSchema
  status: WorkspaceStatusEnum
  authorizedIps: IAuthorizedIp[]
  onlineStatus: WorkspaceOnlineStatusEnum
  userId: string | null
  isShared: boolean
  fileTransferEligible: boolean
  lastErrorLevel: WorkspaceErrorLevelEnum | null
  targetCapacityEnabled: boolean
  lastErrorTitle: string | null
  lastErrorDetails: string | null
  assignedUsers: IWorkspacePermissions[]
  creationDate: string
  modifyDate: string
  schemaDeployed: IWorkspaceSchema | null
  certificateInfo: CertificateInfo<CertificateStatus | null>
  stateChange: StateChange | null
  updateWarning: string | null
  customPeeringAllowed: boolean
  vpcCidr: string | null
  vpcId: string | null
  autoStartupDefinition: AutoStartupDefinition | null
  runningSince?: string
  autoShutdownDefinition: AutoShutdownInformation | null
  subStatus: WorkspaceSubStatus | null
}

export interface Service {
  _discriminator?: DiscriminationKey
  id: string
  type: string
  subtitle: string
  description: string
  onlineStatus: WorkspaceOnlineStatusEnum
  onlineError: string
  applicationLinks: ApplicationLink[]
  userConfigurations: WizardConfigItem[] | null
  runningSince: string | null
  dependsOn: string[]
}

export enum AutoShutdownStatus {
  autoShutdownEnabled = "autoShutdownEnabled",
  nearAutoShutdown = "nearAutoShutdown",
  checkingInactivity = "checkingInactivity",
  scheduled = "autoShutdownScheduled"
}
export interface AutoShutdownInformation {
  inactiveForInMinutes: number
  scheduledToShutdownAt: string
  scheduledAt: string
  inactivityShutdownAt: string
  status: AutoShutdownStatus
}

export interface StateChange {
  changeDate: string
  componentTarget: string
  username: string
  assetId: string
  changedTo: string
}

export interface WorkspaceListItem extends Dictionary<string | null | StateChange> {
  id: string
  title: string
  username: string
  modifyDate: string
  creationDate: string
  onlineStatus: WorkspaceOnlineStatusEnum
  status: WorkspaceStatusEnum
  stateChange: StateChange | null
}

export interface IAllowedApp {
  maxCount?: number
}

export interface WorkspaceRawTemplate {
  id: string
  plan: SuitePlanEnum
  title: string
  description: string
  tags: string[]
  source: string
  schema: IWorkspaceSchema
  date: Date
  instructions: string
  deployPackageId: string
  allowedApps: Dictionary<IAllowedApp>
}

export interface IAssetModel {
  id: string
  key: string
  banners: string[]
  text: string
  description: string
  icon: string
  category: string
  manualUrl: string
  configurations: Record<string, string | IAssetConfigurationDefinition>
  disabled: boolean
  defaultInstanceType?: IAssetInstanceType[]
  availableInstanceTypes?: ModelInstanceType[]
  userConfigurations: WizardConfigItem[] | null
  requireLicenseServer: boolean
  hideFromAssetsToolbox?: boolean
  instancePlatform?: string
}

export interface ModelInstanceType {
  name: `${CloudProvider}`
  instanceTypes: IInstanceType[]
}

export interface AppItem {
  currentCount: number
  maxCount: number
  description: string
  category: string
  icon: string
  isAvailable: boolean
  key: string
  manualUrl: string
  text: string
  extraInformation: {
    inputs: string
    outputs: string
    estimatedCost: string
  }
}

export interface ServiceItem {
  type: string
  text: string
  subtitle: string
  description: string
  icon: string
  category: string
  manualUrl: string
  currentCount: number
  maxCount: number
  isAvailable: boolean
}

export interface ServiceModel {
  type: string
  text: string
  subtitle: string
  description: string
  icon: string
  category: string
  manualUrl: string
  userConfigurations: UpdateAssetUserConfigurationPayload[]
  dependsOn: string[]
}

export interface AddNewAssetPayload {
  assetModelType: string
}

export interface AddNewServicePayload {
  serviceType: string
}

export interface UpdateAssetInstanceTypePayload {
  code: string
}

interface WizardPayloadBase {
  resourceKey: string
  type: WizardWidget | `${WizardWidget}`
  active: boolean
}

export interface PayloadWizardWithValue extends WizardPayloadBase {
  value: string | number | Id | null
}

export interface PayloadWizardWithKeyPairValue extends WizardPayloadBase {
  value: string
}

export interface PayloadWizardWithOptions extends WizardPayloadBase {
  options: Dictionary<string | number>[]
}

export type UpdateAssetUserConfigurationPayload =
  | PayloadWizardWithKeyPairValue
  | PayloadWizardWithValue
  | PayloadWizardWithOptions

export interface UpdateAssetPayload {
  description: string
  allowedInstanceTypes: UpdateAssetInstanceTypePayload[]
  userConfigurations: UpdateAssetUserConfigurationPayload[]
}

export interface UpdateServicePayload {
  description: string
  userConfigurations: UpdateAssetUserConfigurationPayload[]
}

export type LicenseServer = WorkspaceTemplate

export enum VizGraphicApp {
  vectarPlus = "VectarPlus",
  vizTrio = "VizTrio",
  vizMosart = "VizMosart"
}

export interface WorkspacePreBuildSettings {
  vectar: {
    use: boolean
    spaceSize: number
    max: number
    min: number
  }
  ndi: {
    use: boolean
    remote: boolean
    bridge: boolean
  }
  intercom: {
    use: boolean
  }
  vizGraphic: {
    use: boolean
    app: VizGraphicApp
  }
}

export interface LicenseProduct {
  containerId: string | null
  freeLicenses: number
  product: string
  totalLicenses: number
}

export interface ICloudProvider {
  id?: string
  name: CloudProvider
  accountId: string
  instanceTypes?: IInstanceType[]
  deployIamRole: string
  managementIamRole: string
}

export interface IInstanceType {
  code: string
  cpUsGInstances?: number
  cpUsStdInstances?: number
}

export interface IAssetInstanceType extends IInstanceType {
  cloudProviderName: string
}

export interface NdiConfiguration {
  mode: string
  encryptionKey: string
  hostIp: string
  port: number
}

export interface DeployInstanceTypePayload {
  workspaceId: string
  assetId: string
  desiredInstanceType: string
}
